import type { AttachFiles } from "@ext/agent/components/hooks/useAttachFiles";
import { useChatComposerDisabled } from "@ext/agent/components/hooks/useChatComposerDisabled";
import { useOpenAgentSkill } from "@ext/agent/components/skills/useOpenAgentSkill";
import {
	type AgentChatHealthcheckState,
	useAgentChatHealthcheck,
} from "@ext/agent/components/store/AgentChatHealthcheckStore";
import { setSessionSelectedModel, useActiveSessionId } from "@ext/agent/components/store/AgentStore";
import type { AiAgentModel } from "@ext/agent/llm";
import t from "@ext/localization/locale/translate";
import { Alert, AlertDescription, AlertIcon, AlertTitle } from "@ui-kit/Alert";
import { memo, useCallback } from "react";
import { useChatInput } from "../store/ChatStore";
import { ChatInput } from "./ChatInput";

const getUnavailableText = ({ target, reason }: AgentChatHealthcheckState) => {
	if (target === "enterprise-cloud") {
		if (reason === "balance_empty") {
			return {
				title: t("agent.availability-error.enterprise-cloud.balance-empty.title"),
				description: t("agent.availability-error.enterprise-cloud.balance-empty.description"),
			};
		}
		if (reason === "access_denied") {
			return {
				title: t("agent.availability-error.enterprise-cloud.access-denied.title"),
				description: t("agent.availability-error.enterprise-cloud.access-denied.description"),
			};
		}
		if (reason === "access_restricted") {
			return {
				title: t("agent.availability-error.enterprise-cloud.access-restricted.title"),
				description: t("agent.availability-error.enterprise-cloud.access-restricted.description"),
			};
		}
		return {
			title: t("agent.availability-error.enterprise-cloud.title"),
			description: t("agent.availability-error.enterprise-cloud.description"),
		};
	}

	return {
		title: t("agent.availability-error.enterprise.title"),
		description: t("agent.availability-error.enterprise.description"),
	};
};

export const ChatFooter = memo(({ attach }: { attach: AttachFiles }) => {
	const {
		attachments,
		isSending,
		catalogName,
		selectedSkillName,
		browserAllowed,
		onSubmit,
		onCancel,
		onSkillChange,
		onBrowserAllowedChange,
	} = useChatInput();
	const healthcheck = useAgentChatHealthcheck();
	const unavailableText = healthcheck.status === "unavailable" ? getUnavailableText(healthcheck) : null;
	const isModelsLoading = healthcheck.status === "checking";
	const disabled = useChatComposerDisabled();
	const activeSessionId = useActiveSessionId();

	const openSkill = useOpenAgentSkill();

	const handleModelChange = useCallback(
		(model: AiAgentModel) => {
			if (activeSessionId) setSessionSelectedModel(activeSessionId, { type: model.type, name: model.name });
		},
		[activeSessionId],
	);

	const handleSkillTagClick = useCallback(async () => {
		if (selectedSkillName) await openSkill(selectedSkillName);
	}, [selectedSkillName, openSkill]);

	return (
		<div className="shrink-0 pb-2 px-2">
			{unavailableText && (
				<Alert className="mb-2 min-w-0" focus="medium" status="warning">
					<AlertIcon icon="triangle-alert" />
					<AlertTitle>{unavailableText.title}</AlertTitle>
					<AlertDescription>{unavailableText.description}</AlertDescription>
				</Alert>
			)}
			<ChatInput
				attach={attach}
				attachments={attachments}
				browserAllowed={browserAllowed}
				catalogName={catalogName}
				disabled={disabled}
				isModelsLoading={isModelsLoading}
				modelPickAvailable={healthcheck.modelPickAvailable ?? isModelsLoading}
				models={healthcheck.models ?? []}
				onBrowserAllowedChange={onBrowserAllowedChange}
				onCancel={onCancel ?? undefined}
				onModelChange={handleModelChange}
				onSkillChange={onSkillChange}
				onSkillTagClick={handleSkillTagClick}
				onSubmit={onSubmit}
				selectedSkillName={selectedSkillName}
				sending={isSending}
			/>
		</div>
	);
});
